/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "../components/ui/Link";
import { Activity, ChevronRight } from "lucide-react";
import { listRuns, type AgentRun } from "../api/runs";
import { useChrome } from "../contexts/ChromeContext";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { runStatusDot, runStatusLabel, runTime, triggerLabel } from "../lib/runs";
import { cn } from "../lib/utils";

type RunFilter = { key: string; label: string; matches: (status: string) => boolean };

const ALL_FILTER: RunFilter = { key: "ALL", label: "All", matches: () => true };

const FILTERS: RunFilter[] = [
  ALL_FILTER,
  { key: "BOOKED", label: "Booked", matches: (s) => s === "COMPLETED_BOOKED" },
  { key: "HANDED_OFF", label: "Handed off", matches: (s) => s === "COMPLETED_FALLBACK" },
  { key: "RUNNING", label: "Running", matches: (s) => s === "RUNNING" },
  { key: "FAILED", label: "Failed", matches: (s) => s === "FAILED" },
];

export function Runs() {
  const [runs, setRuns] = useState<AgentRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("ALL");

  useChrome({ title: "Runs" }, []);

  useEffect(() => {
    let cancelled = false;
    void listRuns().then((result) => {
      if (cancelled) {
        return;
      }
      if (result.ok) {
        setRuns(result.value);
      } else {
        setError(result.error.message);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const active = FILTERS.find((option) => option.key === filter) ?? ALL_FILTER;
  const visibleRuns = runs?.filter((run) => active.matches(run.status)) ?? null;

  if (runs === null && error === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="enter text-[15px] text-text-secondary">
        Every time the agent woke up, and what it did.
      </p>

      <ErrorMessage message={error} className="mb-0" />

      {runs !== null && runs.length > 0 && (
        <div className="enter flex flex-wrap items-center gap-3">
          <Select
            className="w-full sm:w-56"
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((option) => ({
              value: option.key,
              label:
                option.key === "ALL"
                  ? "All runs"
                  : `${option.label} (${runs.filter((run) => option.matches(run.status)).length})`,
            }))}
          />
          {visibleRuns !== null && filter !== "ALL" && (
            <p className="text-sm text-text-tertiary" aria-live="polite">
              {visibleRuns.length === 1 ? "1 run" : `${visibleRuns.length} runs`}
            </p>
          )}
        </div>
      )}

      {visibleRuns?.length === 0 ? (
        <EmptyState
          icon={<Activity size={20} />}
          title="No runs yet"
          description="The agent wakes when a slot opens up."
        />
      ) : (
        <ol className="space-y-2.5">
          {visibleRuns?.map((run, index) => (
            <RunRow key={run.id} run={run} index={index} />
          ))}
        </ol>
      )}
    </div>
  );
}

function RunRow({ run, index }: { run: AgentRun; index: number }) {
  const outcome = run.resultSummary?.outcome;
  const excluded = run.resultSummary?.exclusions?.length ?? 0;
  const detail = [
    outcome !== undefined ? outcome.replaceAll("_", " ").toLowerCase() : null,
    excluded > 0 ? `${excluded} excluded` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <li>
      <Link
        to={`/streams/${run.id}`}
        className="group border border-border bg-bg-elevated shadow-card lift enter flex items-center gap-3 rounded-2xl px-4 py-3.5 sm:px-5 sm:py-4"
        style={{ "--enter-delay": `${Math.min(index, 8) * 50 + 60}ms` } as CSSProperties}
      >
        <span className={cn("size-2 shrink-0 rounded-full", runStatusDot(run.status))} aria-hidden />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h2 className="min-w-0 flex-1 truncate font-medium text-text-primary">
              {triggerLabel(run.trigger)}
            </h2>
            <span className="shrink-0 text-xs text-text-tertiary">{runTime(run.startedAt)}</span>
          </div>
          <p className="mt-0.5 truncate text-xs">
            <span className="text-text-secondary">{runStatusLabel(run.status)}</span>
            {detail !== "" && <span className="text-text-tertiary"> · {detail}</span>}
          </p>
        </div>

        <ChevronRight
          size={18}
          className="shrink-0 text-text-tertiary transition-transform group-hover:translate-x-0.5 group-hover:text-text-primary"
        />
      </Link>
    </li>
  );
}
